import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../auth';
import { APIVersion } from '../../constants/keys';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';

const transformErrorResponse = (response: FetchBaseQueryError) =>
  (response as { data?: { errors?: unknown } }).data?.errors;

type SeasonApi = {
  id: string;
  crop_id: string;
  boundary_id?: string;
  harvest_date: string | null;
  sowing_date: string | null;
  variety: string;
};

type FieldSeasonApi = {
  id: string;
  crop_id: string;
  field_id: string;
  boundary_id?: string;
  harvest_date: string | null;
  sowing_date: string | null;
  variety: string;
};

type FieldApi = {
  id: string;
  name?: string;
  seasons: SeasonApi[] | null;
}

type BoundariesApi = {
  id: string;
  origin: string;
  origin_id: string | null;
  area: number;
  geom: {
    type: 'MultiPolygon';
    coordinates: number[][][][];
  };
};

interface GetFieldsArgs {
  query?: string;
  ordering?: string;
  page?: number;
  per_page?: number;
}

interface UpdateFieldArgs {
  fieldId: string;
  name: string;
}

interface UpdateSeasonArgs {
  boundaryId: string;
  fieldId: string;
  cropId: string;
  variety: string;
  sowingDate: string;
  harvestDate: string;
}

interface EditSeasonArgs extends UpdateSeasonArgs {
  id: string
}

interface GetBoundariesArgs {
  harvest_date__gte?: string;
  has_field_season?: boolean;
  ordering?: string;
  page?: number;
  per_page?: number;
  sowing_date__lte?: string;
}

interface CreateBoundariesArgs {
  coordinates: number[][][][];
}

interface EditBoundariesArgs extends CreateBoundariesArgs {
  id: string;
}

interface GetFieldsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FieldApi[];
}

interface GetBoundariesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BoundariesApi[];
}

export const fieldsApi = createApi({
  reducerPath: 'fieldsApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    // Fields
    getFields: builder.query<FieldApi[], GetFieldsArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/fields`,
        params,
      }),
      transformResponse: (data: GetFieldsResponse) => data.results,
    }),
    createField: builder.mutation<FieldApi, { name: string }>({
      query: ({ name }) => ({
        url: `/core-api/${APIVersion}/fields`,
        method: 'POST',
        body: { name },
      }),
      transformErrorResponse,
    }),
    updateField: builder.mutation<FieldApi, UpdateFieldArgs>({
      query: ({ fieldId, name }) => ({
        url: `/core-api/${APIVersion}/fields/${fieldId}`,
        method: 'PATCH',
        body: { name },
      }),
      transformErrorResponse,
    }),
    deleteField: builder.mutation<void, { fieldId: string }>({
      query: ({ fieldId }) => ({
        url: `/core-api/${APIVersion}/fields/${fieldId}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),

    // Seasons
    createSeason: builder.mutation<FieldSeasonApi, UpdateSeasonArgs>({
      query: ({ boundaryId, fieldId, cropId, variety, sowingDate, harvestDate }) => ({
        url: `/core-api/${APIVersion}/field-seasons`,
        method: 'POST',
        body: {
          boundary_id: boundaryId,
          field_id: fieldId,
          crop_id: cropId,
          variety,
          sowing_date: sowingDate,
          harvest_date: harvestDate,
        },
      }),
      transformErrorResponse,
    }),
    editSeason: builder.mutation<FieldSeasonApi, EditSeasonArgs>({
      query: ({ id, boundaryId, fieldId, cropId, variety, sowingDate, harvestDate }) => ({
        url: `/core-api/${APIVersion}/field-seasons/${id}`,
        method: 'PATCH',
        body: {
          boundary_id: boundaryId,
          field_id: fieldId,
          crop_id: cropId,
          variety,
          sowing_date: sowingDate,
          harvest_date: harvestDate,
        },
      }),
      transformErrorResponse,
    }),
    deleteSeason: builder.mutation<void, { seasonId: string }>({
      query: ({ seasonId }) => ({
        url: `/core-api/${APIVersion}/field-seasons/${seasonId}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),

    // Boundaries
    getBoundaries: builder.query<BoundariesApi[], GetBoundariesArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/boundaries`,
        params,
      }),
      transformResponse: (data: GetBoundariesResponse) => data.results,
    }),

    createBoundaries: builder.mutation<BoundariesApi, CreateBoundariesArgs>({
      query: ({ coordinates }) => ({
        url: `/core-api/${APIVersion}/boundaries`,
        method: 'POST',
        body: {
          origin: 'drawn',
          geom: {
            type: 'MultiPolygon',
            coordinates: coordinates.length > 1 ? [coordinates] : coordinates,
          },
        },
      }),
      transformErrorResponse,
    }),

    editBoundaries: builder.mutation<BoundariesApi, EditBoundariesArgs>({
      query: ({ id, coordinates }) => ({
        url: `/core-api/${APIVersion}/boundaries/${id}`,
        method: 'PATCH',
        body: {
          geom: {
            type: 'MultiPolygon',
            coordinates: coordinates.length > 1 ? [coordinates] : coordinates,
          },
        },
      }),
      transformErrorResponse,
    }),

    getBoundariesId: builder.query<BoundariesApi, { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/boundaries/${id}`,
      }),
    }),
  }),
});

export const {
  useGetFieldsQuery,
  useCreateFieldMutation,
  useUpdateFieldMutation,
  useDeleteFieldMutation,

  useCreateSeasonMutation,
  useEditSeasonMutation,
  useDeleteSeasonMutation,

  useGetBoundariesQuery,
  useCreateBoundariesMutation,
  useEditBoundariesMutation,
  useGetBoundariesIdQuery,
} = fieldsApi;
